import cn from 'classnames';
import dayjs from 'dayjs';
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';
import { CarouselJsonLd } from 'next-seo';
import { useRouter } from 'next/dist/client/router';
import Head from 'next/head';
import Image from 'next/image';
import { MouseEventHandler, useEffect, useMemo } from 'react';
import useSWR from 'swr';

import { DownOutlined, PlayCircleFilled } from '@ant-design/icons';

import BillboardFilters from '~components/Billboard/BillboardFilters';
import Schedules from '~components/Billboard/Schedules';
import ComingSoon from '~components/Movies/ComingSoon';
import PresaleCountDown from '~components/Movies/PresaleCountDown';
import EmptyState from '~components/ui/EmptyState';
import Gallery from '~components/ui/Gallery';
import Layout from '~components/ui/Layout';
import Loading from '~components/ui/Loading';
import Modal from '~components/ui/Modal';
import Poster from '~components/ui/Poster';
import { getFromAPI, multipleReq } from '~utils/api/common';
import parseQuery from '~utils/api/parseQuery';
import removeHTML from '~utils/api/removeHTML';
import generateImagePlaceholder from '~utils/generateImagePlaceholder';
import getYoutubeEmbedUrl from '~utils/getYoutubeEmbedUrl';
import useResponsive from '~utils/hooks/useResponsive';
import useToggleState from '~utils/hooks/useToggleState';
import { NormalizedComingSoon } from '~utils/normalizers/normalizeComingSoon';
import normalizeMovie, { NormmalizedMovie } from '~utils/normalizers/normalizeMovie';

import { Cinema, Movie, Show } from '~types';

import s from '~styles/pages/Movie.module.css';

const extras = {
  duration: 'Duración',
  rated: 'Clasificación',
  director: 'Director',
  photographyDirector: 'Director de fotografía',
  musicDirector: 'Director de música',
  nationality: 'Nacionalidad',
  actors: 'Reparto',
  language: 'Lenguaje',
  year: 'Año',
  genre: 'Género',
};

interface GSProps {
  movie: NormmalizedMovie;
  initialDates: { date: string }[];
  initialShows: Show[];
  cinemas: Array<Partial<Cinema>>;
  cinemaId: string;
  comingSoon: NormalizedComingSoon[];
  tz: string;
}

const getDate = (date: string | undefined, tz: string) => {
  const now = dayjs().tz(tz);
  if (!date) return now.format();
  const firstDate = dayjs(date).tz(tz);
  return (firstDate.isAfter(now.subtract(20, 'minutes')) ? firstDate : now).format();
};

const dateRegex = /\d{4}/g;
const slugRegex = /^[a-zA-Z0-9\-_]+$/;

export const getStaticProps: GetStaticProps<GSProps> = async (ctx) => {
  if (
    !`${ctx?.params?.CinemaSlug}`.match(dateRegex) &&
    !`${ctx?.params?.CinemaSlug}`.match(slugRegex)
  ) {
    return {
      notFound: true,
    };
  }
  const CinemaSlug = `${ctx?.params?.CinemaSlug}`.match(dateRegex) ? '' : ctx.params?.CinemaSlug;
  const { slug } = ctx.params as { slug: string };
  const movie = await getFromAPI(`/movies/${slug}`);
  const selectedCinema =
    CinemaSlug || ctx?.params?.c || ctx?.params?.CinemaId
      ? await getFromAPI(`/cinemas/${CinemaSlug || ctx?.params?.c || ctx?.params?.CinemaId}`).catch(
          () => null,
        )
      : null;

  const CinemaId = selectedCinema?.id || ctx?.params?.c || ctx?.params?.CinemaId || '';
  // const cinema = cinemaId ? `?CinemaId=${cinemaId}` : '';
  const { dates, cinemas, comingSoon } = await multipleReq<
    Omit<GSProps, 'dates'> & { dates: Array<{ date: string }> }
  >({
    dates: `/shows/dates?${parseQuery({ MovieId: movie.id, CinemaId })}`,
    cinemas: '/cinemas/list',
    comingSoon: `/movies/coming-soon?${parseQuery({ CinemaId })}`,
  });

  const tz = cinemas.find((item) => `${item.id}` === `${CinemaId}`)?.timezone || 'America/Mazatlan';
  const date = getDate(
    !CinemaSlug
      ? (ctx.params?.CinemaSlug as string)
      : (ctx.params?.date as string) || dates?.[0]?.date || dayjs().format(),
    tz,
  );

  const initialShows = await getFromAPI(
    `/shows/movie/${movie.id}?${parseQuery({
      CinemaId,
      date,
    })}`,
  );
  return {
    props: {
      movie: normalizeMovie(movie),
      initialDates: dates,
      initialShows,
      cinemas,
      cinemaId: CinemaId,
      comingSoon,
      tz,
      fallback: {
        '/cinemas/list': cinemas,
      },
    },
    revalidate: 60, // 1 minute.
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

type Props = InferGetStaticPropsType<typeof getStaticProps>;

const MoviePage: NextPage<Props> = ({
  movie,
  initialDates,
  initialShows,
  cinemas,
  cinemaId,
  comingSoon,
  tz,
}) => {
  const cinemaMovie = movie?.CinemaMovies?.find((movieData) => movieData.enablePresale);
  const isAfterPremiere = dayjs().isAfter(dayjs(cinemaMovie?.premiereAt).startOf('day'));
  const isAfterPresaleStart = dayjs().isAfter(dayjs(cinemaMovie?.presaleStartAt).startOf('day'));
  const isAfterPreview = cinemaMovie?.previewAt
    ? dayjs().isAfter(dayjs(cinemaMovie?.previewAt))
    : true;
  const { query } = useRouter();

  const jsonLdForMovie = useMemo(() => {
    return [
      {
        name: movie.title,
        image: movie.poster?.thumbnail,
        url: `${process.env.NEXT_PUBLIC_APP_URL}/pelicula/${movie.slug}`,
        dateCreated: dayjs(movie.createdAt).format('YYYY-MM-DD'),
        director: {
          name: movie.director,
        },
      },
    ];
  }, [movie]);
  const CinemaId = query?.c || query?.CinemaId || cinemaId;

  const { data: newDates } = useSWR(`/shows/dates?CinemaId=${CinemaId}&MovieId=${movie.id}`, {
    revalidateOnMount: false,
    keepPreviousData: true,
  });
  const dates = (newDates || initialDates)?.map((date: { date: string }) => date?.date);

  const { data: shows, isValidating: loading } = useSWR<Show[]>(
    `/billboard?${parseQuery({
      MovieId: `${movie.id}`,
      ...(CinemaId ? { CinemaId: `${CinemaId}` } : {}),
      ...(query?.d || query?.date
        ? { date: query.d || query.date }
        : {
            date: getDate(dates?.[0] || initialDates?.[0] || dayjs().format(), tz),
          }),
    })}`,
    {
      fetcher: (url) =>
        fetch(`${process.env.NEXT_PUBLIC_APP_URL}/api/${url}`).then((res) => res.json()),
      revalidateOnMount: false,
      keepPreviousData: true,
      fallbackData: initialShows,
    },
  );

  useEffect(() => {
    if (window.location.hash === '#trailer') {
      toggleTrailer();
    }
  }, []);
  const [showTrailer, toggleTrailer] = useToggleState(false);
  const [showExtras, toggleExtras] = useToggleState(false);
  const { isMobile } = useResponsive();
  // Filtra los datos extras que si se llenaron en la película.
  // @ts-ignore
  const extraInfo = Object.keys(extras).filter((dataKey) => !!movie[dataKey as keyof Movie]);
  const showsByCinema: Record<string, Show[]> = {};

  (shows || initialShows)?.forEach((show) => {
    if (!showsByCinema[show.CinemaId]) {
      showsByCinema[show.CinemaId] = [show];
    } else {
      showsByCinema[show.CinemaId].push(show);
    }
  });
  const getCinemaById = (id: number): Partial<Cinema> | undefined =>
    cinemas.find((cinema) => cinema.id === id);

  const handleOpenTrailer: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    toggleTrailer();
  };

  const currentCinema = getCinemaById(Number(CinemaId));
  const pageTitle = `${movie?.title} ${
    currentCinema?.name ? '| ' + currentCinema?.name : ''
  } | ${dayjs(
    (query?.CinemaSlug as string)?.match(dateRegex)
      ? query?.CinemaSlug
      : query?.d || (query?.date as string) || dates?.[0] || initialDates?.[0] || dayjs(),
  ).format('DD [de] MMMM, YYYY')}`;

  const date = (query?.CinemaSlug as string)?.match(dateRegex)
    ? query?.CinemaSlug
    : query?.d || (query?.date as string) || dates?.[0] || initialDates?.[0] || dayjs().format();

  return (
    <Layout
      meta={{
        title: pageTitle,
        description: removeHTML(`${movie?.synopsis}`),
        ogImage: `${process.env.NEXT_PUBLIC_APP_URL}/api/og/movie/${movie.slug}`,
      }}
    >
      <Head>
        <link rel="canonical" href={`${process.env.NEXT_PUBLIC_APP_URL}/pelicula/${movie?.slug}`} />
      </Head>

      <CarouselJsonLd ofType="movie" data={jsonLdForMovie} />
      {movie?.banner?.url && (
        <div className={s.banner}>
          <Image
            src={movie?.banner?.url}
            fill
            alt={movie?.title}
            className="object-cover"
            placeholder="blur"
            blurDataURL={generateImagePlaceholder(1920, 700)}
          />
          <div className="bannerGradient" />
        </div>
      )}
      <div className={`${s.mainGrid} ${!movie?.banner?.url && 'mt-8 md:mt-40'}`}>
        {movie.poster && <Poster data={movie.poster} className={s.poster} />}

        <div className={s.movieTitles}>
          <div>
            <h1 className={s.title}>{movie.title}</h1>
            {movie.originalTitle && <h2 className={s.originalTitle}>{movie.originalTitle}</h2>}
            {!!cinemaMovie?.previewAt && !isAfterPreview && isAfterPresaleStart && (
              <PresaleCountDown
                date={cinemaMovie?.previewAt}
                type="preview"
                size={`${isMobile ? 'small' : 'normal'}`}
              />
            )}
            {isAfterPreview &&
              cinemaMovie?.premiereAt &&
              !isAfterPremiere &&
              isAfterPresaleStart && (
                <PresaleCountDown
                  date={cinemaMovie?.premiereAt}
                  type="premiere"
                  size={`${isMobile ? 'small' : 'normal'}`}
                />
              )}
            {cinemaMovie?.presaleStartAt && !isAfterPresaleStart && (
              <PresaleCountDown
                date={cinemaMovie?.presaleStartAt}
                type="presale"
                size={`${isMobile ? 'small' : 'normal'}`}
              />
            )}
          </div>
          {movie.trailer && (
            <a
              href={movie.trailer}
              className={`trailer ${s.trailerBtn}`}
              onClick={handleOpenTrailer}
            >
              <PlayCircleFilled />
              <span>Ver Trailer</span>
            </a>
          )}
        </div>
        <div className={s.synopsisWrapper}>
          {movie.synopsis && (
            <>
              <h3 className="callout">Sinopsis</h3>
              <div className={s.synopsis} dangerouslySetInnerHTML={{ __html: movie.synopsis }} />
            </>
          )}
        </div>
        {!!movie?.gallery?.length && (
          <div className={s.galleryWrapper}>
            <Gallery images={movie.gallery} />
          </div>
        )}
      </div>
      <div className={s.showsGrid}>
        {!!extraInfo.length && (
          <div className={s.infoExtra}>
            <dl className={showExtras ? 'block' : 'hidden lg:block'}>
              {extraInfo.map((dataKey) => (
                <div key={dataKey}>
                  <dt key={dataKey} className="h5">
                    {extras[dataKey as keyof typeof extras]}
                  </dt>
                  {/*@ts-ignore */}
                  <dd className="mb-4 text-support">{`${movie[dataKey as keyof Movie]}`}</dd>
                </div>
              ))}
            </dl>
            <button className={s.infoExtraToggle} onClick={toggleExtras}>
              <span>{showExtras ? 'Ocultar detalles' : 'Mostrar más detalles'}</span>
              <DownOutlined
                className={cn({ ['rotate-180']: showExtras }, 'transition-transform')}
              />
            </button>
          </div>
        )}
        <div className={s.shows}>
          <div>
            <h4 className="h2">No hagas fila, compra tus boletos en línea.</h4>
            <p>Selecciona un horario para comprar tus boletos</p>
          </div>
          <Loading show={loading} size="large" className="!bg-white/50 dark:!bg-neutral-900/50">
            <BillboardFilters
              baseUrl={`/pelicula/${movie.slug}`}
              dates={dates || initialDates}
              onChange={console.log}
              cinema={CinemaId as string}
              date={date}
              baseHrefPath={`/pelicula/${movie.slug}/`}
              className={s.billboardFilters}
              revalidateCinemasOnMount
            />
          </Loading>
          {Object.keys(showsByCinema)?.length ? (
            Object.keys(showsByCinema).map((id) => {
              const cinema = getCinemaById(Number(id));
              return (
                <div key={id} className="mb-12">
                  <h4 className="h2 mb-4 border-b border-line">{cinema?.name}</h4>
                  {!(query?.c || query?.CinemaId) && !cinema?.onlineSale && (
                    <div className="col-span-2 text-center border-amber-400 bg-warning my-8 p-4 rounded">
                      <span className="h3 block">Venta en línea no disponible</span>
                      <span>
                        La venta de boletos en línea no esta disponible por el momento para este
                        complejo.
                      </span>
                    </div>
                  )}
                  <Schedules
                    shows={showsByCinema[id]}
                    theme="dark"
                    timezone={cinema?.timezone}
                    movieSlug={movie.slug as string}
                    disabled={!cinema?.onlineSale}
                  />
                </div>
              );
            })
          ) : (
            <EmptyState
              type="movie"
              title="En este momento no hay horarios disponibles para esta película"
              description="Selecciona otro cine o día."
              className="py-32"
            />
          )}
        </div>
      </div>
      {movie.trailer && (
        <Modal onClose={toggleTrailer} open={showTrailer}>
          <div className="h-[90vh] w-[90vw]">
            <iframe
              className="absolute inset-0 bg-black py-12"
              width="100%"
              height="100%"
              src={getYoutubeEmbedUrl(movie.trailer)}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </Modal>
      )}
      <ComingSoon data={comingSoon.filter((item) => item.id !== movie.id)} />
    </Layout>
  );
};

export default MoviePage;
